/**
 * Permission codes the HR screens declare an interest in.
 *
 * These are the dot-strings `internal/hr/permissions.go` registers with the RBAC
 * catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them once here
 * means a renamed permission is a compile error rather than a silently invisible
 * button.
 *
 * **Three of them guard the attacked surface and are never inferred from each
 * other.** `internal/hr`'s own `permissions.go` says why: a bank account or a
 * statutory id is changed by one person and applied by a *second*, and looking
 * at one in the clear is a third, separately audited act.
 *
 *  - `detailChange` raises a request. It applies nothing. A holder of this code
 *    alone can never alter what an employee is paid into.
 *  - `detailApprove` is the second pair of eyes. The server refuses
 *    self-approval twice over (the approval engine, then `DecideDetailChange`),
 *    so a user holding both codes still cannot approve their own request — and
 *    the screens say so before the button rather than after the 422.
 *  - `bankReveal` decrypts. Every use writes an `audit.ActionSensitiveReveal`
 *    row naming the reader and their stated reason.
 *
 * All three declare `RequiresMFA`, so a session with no recent second factor is
 * refused with `ERR_STEP_UP_REQUIRED` — a step to take, not a wall.
 *
 * Four codes here belong to other modules and are listed because these screens
 * cannot do their job without them:
 *
 *  - `org.company.read` — every employee belongs to a company, and it scopes
 *    every list and every payroll run raised from these screens.
 *  - `org.unit.read` — departments, cost centres and branches are one
 *    permission in `internal/org`, because they are one table. This is the org
 *    assignment §8.12 asks for, and the dimensions payroll's journal lines
 *    carry. A user who cannot read them is told the assignment cannot be shown
 *    rather than shown an empty picker implying the employee is unassigned.
 */

export const HR_PERMISSIONS = {
  employeeRead: 'hr.employee.read',
  employeeCreate: 'hr.employee.create',
  employeeUpdate: 'hr.employee.update',
  employeeDelete: 'hr.employee.delete',

  /** Raise a bank / statutory-id change request. Applies nothing on its own. */
  detailChange: 'hr.employee.detail.change',
  /** The second pair of eyes that actually applies one. */
  detailApprove: 'hr.employee.detail.approve',
  /** Decrypt an employee's payroll bank account. Audited, every time. */
  bankReveal: 'hr.employee.bank.reveal',

  leaveTypeRead: 'hr.leave_type.read',
  leaveTypeManage: 'hr.leave_type.manage',

  leaveRead: 'hr.leave.read',
  leaveRequest: 'hr.leave.request',
  leaveDecide: 'hr.leave.decide',
  leaveCancel: 'hr.leave.cancel',
  /** Run the monthly accrual against a chosen date, on demand. */
  leaveAccrualRun: 'hr.leave.accrual.run',

  attendanceRead: 'hr.attendance.read',
  attendanceCreate: 'hr.attendance.create',
  attendanceImport: 'hr.attendance.import',
  /** Approving a row approves the overtime hours payroll will price. */
  attendanceApprove: 'hr.attendance.approve',

  overtimeRuleRead: 'hr.overtime_rule.read',
  overtimeRuleManage: 'hr.overtime_rule.manage',

  requisitionRead: 'hr.requisition.read',
  requisitionCreate: 'hr.requisition.create',
  requisitionUpdate: 'hr.requisition.update',

  candidateRead: 'hr.candidate.read',
  candidateCreate: 'hr.candidate.create',
  candidateUpdate: 'hr.candidate.update',
  /** Turning a candidate into an employee with real compensation. */
  candidateConvert: 'hr.candidate.convert',

  offerRead: 'hr.offer.read',
  offerCreate: 'hr.offer.create',
  offerDecide: 'hr.offer.decide',

  /* ------------------------------------------------- other modules' codes */

  companyRead: 'org.company.read',
  /** Departments, cost centres and branches — one code, one table. */
  unitRead: 'org.unit.read',
} as const

export type HrPermission = (typeof HR_PERMISSIONS)[keyof typeof HR_PERMISSIONS]
